import { useCallback, useEffect, useRef, useState } from 'react';

const supabaseUrl =
  import.meta.env.VITE_SUPABASE_URL ?? 'http://127.0.0.1:54321';
const EDGE_FN_URL = `${supabaseUrl}/functions/v1/admin-household-accuracy`;

type Status = 'idle' | 'loading' | 'success' | 'error';

export type AccuracyRating = 'A' | 'B' | 'C' | 'D';

export interface AccuracyWeek {
  week_start: string;
  decided: number;
  unchanged: number;
  /** 0–1, null with nothing decided. */
  precision: number | null;
}

/** One position bucket of a family's learning curve (Phase 3a). */
export interface AccuracyCurveBucket {
  /** '1-10' | '11-20' | '21-40' | '41+' — decided items in decision order. */
  bucket: string;
  decided: number;
  unchanged: number;
  /** 0–1, null with nothing decided. */
  precision: number | null;
}

export interface HouseholdAccuracy {
  household_id: string;
  primary_email: string | null;
  primary_name: string | null;
  decided: number;
  unchanged: number;
  /** 0–1, null with nothing decided. */
  precision: number | null;
  /** null = not enough data. */
  rating: AccuracyRating | null;
  breakdown: {
    dismissed: number;
    child_corrected: number;
    title_edited: number;
    date_corrected: number;
    location_corrected: number;
    type_changed: number;
  };
  /** Every correction flag over decided items, or null with nothing decided. */
  corrections_per_item: number | null;
  /** End dates the parent set on weekly rules the model left open. */
  recurrence_until_set: number;
  /** Items the parent changed / deleted on the calendar after approving. */
  late: { edited: number; deleted: number };
  proposals: {
    accepted: number;
    dismissed: number;
    kept_as_new: number;
    /** Update/cancel claims refused by the safety net at extraction. */
    refused: number;
  };
  weeks: AccuracyWeek[];
  /** Precision by decided-item position — the family's learning curve. */
  curve: AccuracyCurveBucket[];
  /** Current week minus the three before it, on the 0–1 scale; or null. */
  trend: number | null;
  memory_facts: Record<string, number>;
}

export interface HouseholdAccuracyResponse {
  generated_at: string;
  context_level: number;
  /** First recorded discard (ISO), or null — discards count from here. */
  dismissals_since: string | null;
  overall: { decided: number; unchanged: number; precision: number | null };
  most_improved: string[];
  needs_attention: string[];
  households: HouseholdAccuracy[];
}

interface UseAdminHouseholdAccuracyResult {
  status: Status;
  data: HouseholdAccuracyResponse | null;
  error: string | null;
  lastFetchedAt: Date | null;
  refetch: () => void;
}

/**
 * Per-household extraction accuracy from the password-gated
 * admin-household-accuracy edge function. Mirrors useAdminLlmSpend: one GET,
 * refreshed every 5 minutes while the tab is visible (the numbers move with
 * Review decisions, not by the second).
 */
export function useAdminHouseholdAccuracy(
  password: string,
): UseAdminHouseholdAccuracyResult {
  const [status, setStatus] = useState<Status>('idle');
  const [data, setData] = useState<HouseholdAccuracyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const inFlight = useRef(false);

  const fetchOnce = useCallback(async () => {
    if (!password || inFlight.current) return;
    inFlight.current = true;
    setStatus('loading');
    try {
      const res = await fetch(EDGE_FN_URL, {
        method: 'GET',
        headers: { 'X-Admin-Password': password },
      });
      if (!res.ok) {
        const body = await res
          .json()
          .catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json: HouseholdAccuracyResponse = await res.json();
      setData(json);
      setLastFetchedAt(new Date());
      setStatus('success');
      setError(null);
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      inFlight.current = false;
    }
  }, [password]);

  useEffect(() => {
    fetchOnce();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchOnce();
    }, 300_000);
    return () => clearInterval(interval);
  }, [fetchOnce]);

  return { status, data, error, lastFetchedAt, refetch: fetchOnce };
}
